import React, { useState, useEffect } from "react";
import {
  getMarkupRules, createMarkupRule, updateMarkupRule, deleteMarkupRule,
} from "../../services/api";
import { FaPlus, FaEdit, FaTrash, FaSave, FaTimes, FaPercentage, FaMoneyBillWave } from "react-icons/fa";

import { useConfirm } from "../common/ConfirmDialog";
// ── Types ─────────────────────────────────────────────────────────────────────

interface MarkupRule {
  id: number;
  name: string;
  routeType: string;
  cabinClass: string;
  airlineCode?: string | null;
  fixedFee: number;
  percentageFee: number;
  minFee: number;
  maxFee: number;
  currency: string;
  applyPer: string;
  isActive: boolean;
  priority: number;
}

const EMPTY_RULE: Omit<MarkupRule, "id"> = {
  name: "", routeType: "all", cabinClass: "all", airlineCode: null,
  fixedFee: 0, percentageFee: 0, minFee: 0, maxFee: 0,
  currency: "AED", applyPer: "passenger", isActive: true, priority: 0,
};

const ROUTE_OPTIONS = [
  { value: "all", label: "All Routes" },
  { value: "domestic", label: "Domestic" },
  { value: "international", label: "International" },
  { value: "gcc", label: "GCC" },
];

const CABIN_OPTIONS = [
  { value: "all", label: "All Cabins" },
  { value: "Y", label: "Economy" },
  { value: "C", label: "Business" },
  { value: "F", label: "First Class" },
];

const APPLY_OPTIONS = [
  { value: "passenger", label: "Per Passenger" },
  { value: "booking", label: "Per Booking" },
];

// ── Shared helpers (defined outside component to avoid re-creation) ───────────

const inputCls = "w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent";

function previewFee(rule: Partial<MarkupRule>): string {
  const sampleBaseFare = 1000;
  let fee = (rule.fixedFee ?? 0) + (sampleBaseFare * (rule.percentageFee ?? 0) / 100);
  if ((rule.minFee ?? 0) > 0 && fee < (rule.minFee ?? 0)) fee = rule.minFee ?? 0;
  if ((rule.maxFee ?? 0) > 0 && fee > (rule.maxFee ?? 0)) fee = rule.maxFee ?? 0;
  return fee.toFixed(2);
}

const Field: React.FC<{ label: string; children: React.ReactNode }> = ({ label, children }) => (
  <div>
    <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1">{label}</label>
    {children}
  </div>
);

const RuleForm: React.FC<{
  data: Partial<MarkupRule>;
  onChange: (d: Partial<MarkupRule>) => void;
  onSave: () => void;
  onCancel: () => void;
  saveLabel: string;
  saving: boolean;
}> = ({ data, onChange, onSave, onCancel, saveLabel, saving }) => (
  <div className="bg-white border border-gray-200 rounded-xl p-5 space-y-4">
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
      <Field label="Rule Name">
        <input className={inputCls} value={data.name ?? ""} onChange={(e) => onChange({ ...data, name: e.target.value })} placeholder="e.g. Default Service Fee" />
      </Field>
      <Field label="Route Type">
        <select className={inputCls} value={data.routeType ?? "all"} onChange={(e) => onChange({ ...data, routeType: e.target.value })}>
          {ROUTE_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
        </select>
      </Field>
      <Field label="Cabin Class">
        <select className={inputCls} value={data.cabinClass ?? "all"} onChange={(e) => onChange({ ...data, cabinClass: e.target.value })}>
          {CABIN_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
        </select>
      </Field>
      <Field label="Airline Code (blank = all)">
        <input className={inputCls} value={data.airlineCode ?? ""} onChange={(e) => onChange({ ...data, airlineCode: e.target.value.toUpperCase() || null })} placeholder="e.g. EK" maxLength={3} />
      </Field>
      <Field label="Fixed Fee">
        <div className="relative">
          <span className="absolute left-3 top-2.5 text-xs text-gray-400">{data.currency ?? "AED"}</span>
          <input type="number" className={`${inputCls} pl-12`} value={data.fixedFee ?? 0} onChange={(e) => onChange({ ...data, fixedFee: Number(e.target.value) })} min={0} step={0.01} />
        </div>
      </Field>
      <Field label="Percentage Fee (%)">
        <div className="relative">
          <input type="number" className={inputCls} value={data.percentageFee ?? 0} onChange={(e) => onChange({ ...data, percentageFee: Number(e.target.value) })} min={0} max={100} step={0.01} />
          <span className="absolute right-3 top-2.5 text-xs text-gray-400">%</span>
        </div>
      </Field>
      <Field label="Min Fee">
        <input type="number" className={inputCls} value={data.minFee ?? 0} onChange={(e) => onChange({ ...data, minFee: Number(e.target.value) })} min={0} step={0.01} />
      </Field>
      <Field label="Max Fee (0 = no cap)">
        <input type="number" className={inputCls} value={data.maxFee ?? 0} onChange={(e) => onChange({ ...data, maxFee: Number(e.target.value) })} min={0} step={0.01} />
      </Field>
      <Field label="Currency">
        <select className={inputCls} value={data.currency ?? "AED"} onChange={(e) => onChange({ ...data, currency: e.target.value })}>
          {["AED", "USD", "INR", "EUR", "GBP", "SAR", "QAR", "BHD", "KWD", "OMR"].map((c) => <option key={c} value={c}>{c}</option>)}
        </select>
      </Field>
      <Field label="Apply Per">
        <select className={inputCls} value={data.applyPer ?? "passenger"} onChange={(e) => onChange({ ...data, applyPer: e.target.value })}>
          {APPLY_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
        </select>
      </Field>
      <Field label="Priority (higher = matched first)">
        <input type="number" className={inputCls} value={data.priority ?? 0} onChange={(e) => onChange({ ...data, priority: Number(e.target.value) })} />
      </Field>
      <Field label="Active">
        <label className="flex items-center gap-2 mt-1 cursor-pointer">
          <input type="checkbox" checked={data.isActive !== false} onChange={(e) => onChange({ ...data, isActive: e.target.checked })} className="accent-primary w-4 h-4" />
          <span className="text-sm">{data.isActive !== false ? "Active" : "Inactive"}</span>
        </label>
      </Field>
    </div>
    <div className="bg-blue-50 border border-blue-200 rounded-lg px-4 py-3 text-sm text-blue-800">
      <strong>Preview:</strong> On a {data.currency ?? "AED"} 1,000 base fare → Service fee = <strong>{data.currency ?? "AED"} {previewFee(data)}</strong> {data.applyPer === "passenger" ? "per passenger" : "per booking"}
    </div>
    <div className="flex gap-3 justify-end">
      <button onClick={onCancel} disabled={saving} className="px-4 py-2 text-sm font-semibold text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50 transition">
        <FaTimes className="inline mr-1" size={11} /> Cancel
      </button>
      <button onClick={onSave} disabled={saving} className="px-5 py-2 text-sm font-semibold text-white bg-primary rounded-lg hover:bg-blue-800 transition disabled:opacity-50">
        <FaSave className="inline mr-1" size={11} /> {saving ? "Saving..." : saveLabel}
      </button>
    </div>
  </div>
);

// ── Component ─────────────────────────────────────────────────────────────────

const MarkupManager: React.FC = () => {
    const confirm = useConfirm();
  const [rules, setRules] = useState<MarkupRule[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [editData, setEditData] = useState<Partial<MarkupRule>>({});
  const [showCreate, setShowCreate] = useState(false);
  const [createData, setCreateData] = useState<Omit<MarkupRule, "id">>(EMPTY_RULE);
  const [saving, setSaving] = useState(false);

  const fetchRules = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await getMarkupRules();
      setRules(res.rules ?? []);
    } catch (err: any) {
      setError(err?.response?.data?.error || err?.message || "Failed to load markup rules");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchRules(); }, []);

  const handleSaveEdit = async () => {
    if (editingId == null) return;
    setSaving(true);
    try {
      await updateMarkupRule(editingId, editData);
      setEditingId(null);
      setEditData({});
      await fetchRules();
    } catch (err: any) {
      setError(err?.response?.data?.error || "Failed to update rule");
    } finally {
      setSaving(false);
    }
  };

  const handleCreate = async () => {
    if (!createData.name.trim()) { setError("Rule name is required"); return; }
    setSaving(true);
    try {
      await createMarkupRule(createData);
      setShowCreate(false);
      setCreateData(EMPTY_RULE);
      await fetchRules();
    } catch (err: any) {
      setError(err?.response?.data?.error || "Failed to create rule");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id: number) => {
    if (!(await confirm({
      title: "Delete this markup rule?",
      message: "Fares will stop using it as soon as it is removed.",
      confirmLabel: "Delete rule",
      tone: "danger",
    }))) return;
    try {
      await deleteMarkupRule(id);
      await fetchRules();
    } catch (err: any) {
      setError(err?.response?.data?.error || "Failed to delete rule");
    }
  };

  const startEdit = (rule: MarkupRule) => {
    setEditingId(rule.id);
    setEditData({ ...rule });
    setShowCreate(false);
  };

  const cancelEdit = () => { setEditingId(null); setEditData({}); };

  // ── Main render ────────────────────────────────────────────────────────────
  return (
    <div className="max-w-screen-xl mx-auto px-4 py-8">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-heading flex items-center gap-2">
            <FaMoneyBillWave className="text-primary" /> Markup Management
          </h1>
          <p className="text-sm text-gray-500 mt-1">Configure service fees added to airline fares</p>
        </div>
        {!showCreate && (
          <button
            onClick={() => { setShowCreate(true); setEditingId(null); setCreateData(EMPTY_RULE); }}
            className="flex items-center gap-2 bg-primary text-white px-5 py-2.5 rounded-lg text-sm font-semibold hover:bg-blue-800 transition"
          >
            <FaPlus size={12} /> New Rule
          </button>
        )}
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 rounded-xl px-4 py-3 mb-5 text-sm flex items-center justify-between">
          <span>{error}</span>
          <button onClick={() => setError(null)} className="text-red-400 hover:text-red-600"><FaTimes /></button>
        </div>
      )}

      {/* Create form */}
      {showCreate && (
        <div className="mb-6">
          <h3 className="text-sm font-bold text-gray-700 mb-3">Create New Rule</h3>
          <RuleForm
            data={createData}
            onChange={(d) => setCreateData(d as Omit<MarkupRule, "id">)}
            onSave={handleCreate}
            onCancel={() => setShowCreate(false)}
            saveLabel="Create Rule"
            saving={saving}
          />
        </div>
      )}

      {/* Rules list */}
      {loading ? (
        <div className="text-center py-12 text-gray-400">Loading rules...</div>
      ) : rules.length === 0 ? (
        <div className="bg-white border border-gray-200 rounded-xl py-16 text-center">
          <FaPercentage className="text-gray-200 text-5xl mx-auto mb-3" />
          <p className="text-gray-500">No markup rules configured</p>
          <p className="text-sm text-gray-400 mt-1">Create your first rule to start charging service fees</p>
        </div>
      ) : (
        <div className="space-y-4">
          {rules.map((rule) => (
            <div key={rule.id}>
              {editingId === rule.id ? (
                <RuleForm
                  data={editData}
                  onChange={setEditData}
                  onSave={handleSaveEdit}
                  onCancel={cancelEdit}
                  saveLabel="Save Changes"
                  saving={saving}
                />
              ) : (
                <div className={`bg-white border rounded-xl overflow-hidden ${rule.isActive ? "border-gray-200" : "border-gray-200 opacity-60"}`}>
                  <div className="px-5 py-4 flex items-start justify-between gap-4">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-2">
                        <h3 className="font-bold text-gray-900">{rule.name}</h3>
                        <span className={`text-[10px] px-2 py-0.5 rounded-full font-semibold ${rule.isActive ? "bg-green-50 text-green-700 border border-green-200" : "bg-gray-100 text-gray-500 border border-gray-200"}`}>
                          {rule.isActive ? "Active" : "Inactive"}
                        </span>
                        <span className="text-[10px] bg-primary/10 text-primary px-2 py-0.5 rounded-full font-semibold">
                          Priority: {rule.priority}
                        </span>
                      </div>

                      <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-6 gap-3 text-sm">
                        <div>
                          <span className="text-[10px] text-gray-400 uppercase block">Fixed Fee</span>
                          <span className="font-semibold text-gray-800">{rule.currency} {rule.fixedFee}</span>
                        </div>
                        <div>
                          <span className="text-[10px] text-gray-400 uppercase block">Percentage</span>
                          <span className="font-semibold text-gray-800">{rule.percentageFee}%</span>
                        </div>
                        <div>
                          <span className="text-[10px] text-gray-400 uppercase block">Min / Max</span>
                          <span className="font-semibold text-gray-800">{rule.minFee} / {rule.maxFee || "No cap"}</span>
                        </div>
                        <div>
                          <span className="text-[10px] text-gray-400 uppercase block">Route</span>
                          <span className="font-semibold text-gray-800 capitalize">{rule.routeType}</span>
                        </div>
                        <div>
                          <span className="text-[10px] text-gray-400 uppercase block">Cabin</span>
                          <span className="font-semibold text-gray-800">{CABIN_OPTIONS.find((c) => c.value === rule.cabinClass)?.label ?? rule.cabinClass}</span>
                        </div>
                        <div>
                          <span className="text-[10px] text-gray-400 uppercase block">Apply</span>
                          <span className="font-semibold text-gray-800 capitalize">{rule.applyPer}</span>
                        </div>
                      </div>

                      {/* Preview */}
                      <div className="mt-2 text-xs text-gray-400">
                        On {rule.currency} 1,000 fare → <span className="text-primary font-semibold">{rule.currency} {previewFee(rule)}</span> {rule.applyPer === "passenger" ? "/ passenger" : "/ booking"}
                        {rule.airlineCode ? ` · Airline: ${rule.airlineCode}` : ""}
                      </div>
                    </div>

                    <div className="flex gap-2 shrink-0">
                      <button onClick={() => startEdit(rule)} className="w-8 h-8 flex items-center justify-center rounded-lg border border-gray-200 text-gray-500 hover:bg-gray-100 hover:text-primary transition">
                        <FaEdit size={12} />
                      </button>
                      <button onClick={() => handleDelete(rule.id)} className="w-8 h-8 flex items-center justify-center rounded-lg border border-gray-200 text-gray-500 hover:bg-red-50 hover:text-red-500 transition">
                        <FaTrash size={12} />
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default MarkupManager;
